import Map from 'ol/Map';
import View, { type ViewOptions } from 'ol/View';
import { defaults as defaultControls } from 'ol/control';
import 'ol/ol.css';
import { transform } from 'ol/proj';
import {
  forwardRef,
  ReactNode,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import {
  OpenLayersContextInterface,
  OpenLayersProvider,
  useOpenLayersContext,
} from './OpenLayersContext';
import { useProjectionContext } from './ProjectionContext';
import { DrawingProvider, useDrawingContext } from './drawing/DrawingContext';
import { defaults as defaultInteractions, DragPan, PinchZoom } from 'ol/interaction';
import { LayerProvider } from './layerManagement/LayerContext';
import { cn } from '@ais-ui/utils';
import { MapStateProvider } from './mapStates/MapStateProvider';
import { useProjectionConfig } from './hooks/useProjectionConfig';
import { useExposeMapForTesting } from './hooks/useExposeMapForTesting';

interface MapContainerProps extends Pick<ViewOptions, 'zoom' | 'center'> {
  children?: ReactNode;
  placeholder?: ReactNode;
  setZoom?: (zoom: number) => void;
  setCenter?: (center: [number, number]) => void;
  displayProjection?: string;
  dataProjection?: string;
  className?: string;
  olMapRef?: React.MutableRefObject<any>; // Access to map outside provider
}

const LayerComponent = ({ children }: { children: ReactNode }) => {
  const { map } = useOpenLayersContext();
  const { isDrawing } = useDrawingContext();

  return (
    <LayerProvider map={map} isDrawing={isDrawing}>
      {children}
    </LayerProvider>
  );
};

function MapContainerComponent(
  {
    children,
    placeholder,
    setZoom,
    setCenter,
    displayProjection,
    dataProjection,
    zoom = 9,
    center = [11, 59],
    className = '',
    olMapRef,
  }: MapContainerProps,
  forwardedRef: Ref<Map>
) {
  const projectionContext = useProjectionContext();

  // Use ProjectionContext as primary source, allow props to override if needed
  const finalDisplayProjection =
    displayProjection ?? projectionContext.displayProjection;
  const finalDataProjection =
    dataProjection ?? projectionContext.dataProjection;

  // Simplified internal state - just the basic properties
  const [mapState, setMapState] = useState<{
    map: Map;
    displayProjection: string;
    dataProjection: string;
  } | null>(null);

  useImperativeHandle(forwardedRef, () => mapState?.map as Map, [mapState]);

  const mapRef = useCallback((node: HTMLDivElement | null) => {
    if (node !== null && mapState === null) {
      // Detect if we're on a mobile device
      const isMobile = window.innerWidth < 768;
      
      // Base interactions
      const interactions = defaultInteractions().extend([
        new DragPan({
          condition: function (event) {
            // Makes panning available for middle mouse button
            return event.originalEvent.button === 1;
          },
        }),
      ]);

      // Add mobile-specific touch interactions
      if (isMobile) {
        interactions.extend([
          new PinchZoom({
            duration: 200,
          }),
          new DragPan({
            kinetic: undefined, // Enables kinetic/momentum scrolling
          }),
        ]);
      }

      const map = new Map({
        controls: defaultControls({ zoom: false }),
        target: node,
        view: new View({
          projection: finalDisplayProjection,
          center: transform(
            center,
            finalDataProjection,
            finalDisplayProjection
          ),
          zoom,
          constrainResolution: true,
          minZoom: 3,
          multiWorld: false,
          showFullExtent: true,
          // Disable rotation - the overlay layer does not support rotated maps yet
          enableRotation: false,
        }),
        layers: [],
        interactions: interactions,
      });

      // Do ONLY use this mapref if the map isn't accessible through the provider / useOpenlayers hook
      if (olMapRef) {
        olMapRef.current = map;
      }

      setMapState({
        map,
        displayProjection: finalDisplayProjection,
        dataProjection: finalDataProjection,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mapState === null) {
      return;
    }

    const handleMoveEnd = () => {
      if (setZoom) {
        const currentZoom = mapState.map.getView().getZoom();
        if (currentZoom) {
          setZoom(currentZoom);
        }
      }

      if (setCenter) {
        const currentCenter = mapState.map.getView().getCenter();
        if (currentCenter) {
          const transformedCenter = transform(
            currentCenter,
            mapState.displayProjection,
            mapState.dataProjection
          ) as [number, number];
          setCenter(transformedCenter);
        }
      }
    };

    mapState.map.on('moveend', handleMoveEnd);

    return () => {
      mapState.map.un('moveend', handleMoveEnd);
    };
  }, [mapState, setZoom, setCenter]);

  useEffect(() => {
    return () => {
      mapState?.map.setTarget(undefined);
    };
  }, [mapState]);

  useEffect(() => {
    if (mapState == null || zoom == null) {
      return;
    }
    mapState.map.getView().setZoom(zoom);
  }, [mapState, zoom]);

  useEffect(() => {
    if (mapState == null || center == null) {
      return;
    }
    mapState.map
      .getView()
      .setCenter(
        transform(center, mapState.dataProjection, mapState.displayProjection)
      );
  }, [center, mapState]);

  // Expose map for E2E testing (dev mode only)
  useExposeMapForTesting(mapState?.map ?? null);

  return (
    <div
      id="map"
      ref={mapRef}
      className={cn('flex w-full h-full relative', className)}
    >
      {mapState != null ? (
        <OpenLayersProvider
          value={{
            map: mapState.map,
            displayProjection: mapState.displayProjection,
            dataProjection: mapState.dataProjection,
          }}
        >
          <MapStateProvider>
            <DrawingProvider>
              <LayerComponent>{children}</LayerComponent>
            </DrawingProvider>
          </MapStateProvider>
        </OpenLayersProvider>
      ) : (
        placeholder ?? null
      )}
    </div>
  );
}

export const MapContainer = forwardRef(MapContainerComponent);
